import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import HeroPlaceholder from '../components/HeroPlaceholder';
import FaqAccordion from '../components/FaqAccordion';
import CtaBand from '../components/CtaBand';
import SEO from '../components/SEO';
import RelatedBand from '../components/RelatedBand';
import MattingCalculator from '../components/MattingCalculator';
import { HERO_MEDIA, SITE } from '../config/media';
import { usePageContent } from '../lib/useContent';
import {
    orgJsonLd,
    breadcrumbJsonLd,
    faqJsonLd,
    ABS_URL,
} from '../lib/seo';

const CATALOG_HERO_FALLBACK = {
    hero: {
        eyebrow: 'Product Catalog',
        headline: 'All Products.',
        body: 'The full spectrum of timber ground-protection matting - manufactured at our Chemainus, BC mill, with sales coordination from Calgary, AB. Coastal Hem-Fir, Manufacturer Direct, custom-spec capability.',
        image_url: HERO_MEDIA.millYard.src,
    },
};

const CATALOG = [
    {
        to: '/access-mats',
        head: 'Traditional Access Mats',
        body: 'Traditional three-ply interlocking access mat.',
        cta: 'Spec Access Mats',
    },
    {
        to: '/rig-mats',
        head: 'Rig Mats',
        body: 'Heavy-duty multi-layer timber with through-rods. Engineered working decks for drilling, workover, frac spreads, production support.',
        cta: 'Spec Rig Mats',
    },
    {
        to: '/crane-mats',
        head: 'Crane Mats',
        body: '#2 & BTR coastal Fir, recessed spot-welded nuts. Premium coastal-fir laminated timber, custom-built to your rigging and geotech spec.',
        cta: 'Spec Crane Mats',
    },
    {
        to: '/quote?mat_type=access&project_type=pipeline',
        head: 'Pipeline Skids',
        body: 'Pipeline skid mats configurable to project specifications. Quoted alongside the access matting package for mainline and well-pad supply.',
        cta: 'Quote Pipeline Skids',
    },
    {
        to: '/quote?mat_type=access',
        head: 'Transition / Ramp Mats',
        body: 'Engineered ramps onto and off of mat corridors. Reduces edge wear, manages vehicle-load handoff between mat surface and gravel road.',
        cta: 'Quote Ramp Mats',
    },
    {
        to: '/contact?topic=sales',
        head: 'Custom Made-to-Order',
        body: 'Built to your engineering. Non-standard sizes, alternate timber grades, custom bolt patterns - our Chemainus, BC mill handles bespoke project requirements.',
        cta: 'Talk to Mill Engineering',
    },
];

const SPEC_TABLE = [
    { product: 'Traditional Access Mat', dim: '8\u2032 \u00D7 14\u2032 \u00D7 5\u2033', material: 'Hem-Fir Hi-Line #2', apps: 'Pipeline ROW, well-site access, muskeg', lead: 'On request' },
    { product: 'Rig Mat (8\u2032 \u00D7 14\u2032)', dim: '8\u2032 \u00D7 14\u2032', material: 'Hem-Fir Hi-Line #2', apps: 'Workover rig, surface holes', lead: 'On request' },
    { product: 'Rig Mat (8\u2032 \u00D7 30\u2032)', dim: '8\u2032 \u00D7 30\u2032', material: 'Hem-Fir Hi-Line #2', apps: 'Drilling pad, frac spread', lead: 'On request' },
    { product: 'Rig Mat (8\u2032 \u00D7 40\u2032)', dim: '8\u2032 \u00D7 40\u2032', material: 'Hem-Fir Hi-Line #2', apps: 'Drilling pad, large rig moves', lead: 'On request' },
    { product: 'Crane Mat (4\u2032 \u00D7 20\u2032 \u00D7 12\u2033)', dim: '4\u2032 \u00D7 20\u2032 \u00D7 12\u2033', material: '#2 & BTR coastal Fir', apps: 'Wind farm, crawler cranes', lead: 'On request' },
    { product: 'Crane Mat (4\u2032 \u00D7 30\u2032 \u00D7 12\u2033)', dim: '4\u2032 \u00D7 30\u2032 \u00D7 12\u2033', material: '#2 & BTR coastal Fir', apps: 'Heavy lift, tower assemblies', lead: 'On request' },
    { product: 'Pipeline Skid', dim: 'To spec', material: 'Hem-Fir Hi-Line #2', apps: 'Mainline, well-pad', lead: 'On request' },
    { product: 'Transition / Ramp Mat', dim: 'Engineered', material: 'Hem-Fir Hi-Line #2', apps: 'Corridor edge handoff', lead: 'On request' },
    { product: 'Custom build-to-spec', dim: 'Any', material: 'Project-specified', apps: 'Engineered to job', lead: 'On request' },
];

const FAQS = [
    {
        q: 'Can Centurion build mats to custom specifications?',
        a: 'Yes. Our Chemainus, BC mill regularly builds to non-standard sizes, alternate timber grades, and custom bolt patterns. Send the engineering spec to sales and we\u2019ll quote.',
    },
    {
        q: 'What\u2019s the lead time on standard 8\u2032 \u00D7 14\u2032 access mats?',
        a: 'Lead time aligns with the mill schedule and your project delivery milestone. The most reliable signal is to share the project window with sales early - Manufacturer Direct gives us visibility into our own schedule, so we can commit firm dates.',
    },
    {
        q: 'Does Centurion sell to matting service companies and resellers?',
        a: 'Yes - Centurion is both a manufacturer and a wholesaler. We supply matting service companies, EPCs, and resellers across Western Canada Manufacturer Direct. See the Procurement Partnerships page for the wholesale / reseller program, including volume pricing and credit-terms framework.',
    },
    {
        q: 'Is there a minimum order quantity?',
        a: 'There is no rigid MOQ. We supply small top-up orders through to multi-thousand-mat infrastructure programs. For wholesale / reseller pricing the typical entry point is in the hundreds of mats per order.',
    },
];

export default function MattingSuppliers() {
    const content = usePageContent('catalog', CATALOG_HERO_FALLBACK);
    const hero = content.hero || CATALOG_HERO_FALLBACK.hero;
    return (
        <>
            <SEO
                title="All Products - Full Centurion Catalog"
                description="The full Centurion Matting Products catalog - access mats, rig mats, crane mats, pipeline skids, transition mats, and built-to-spec custom mats. Manufacturer Direct from our Chemainus, BC mill."
                cmsSlug="seo.catalog"
                path="/catalog"
                image={HERO_MEDIA.millYard.src}
                jsonLd={[
                    orgJsonLd(),
                    breadcrumbJsonLd([
                        { name: 'Home', path: '/' },
                        { name: 'All Products', path: '/catalog' },
                    ]),
                    faqJsonLd(FAQS),
                    {
                        '@context': 'https://schema.org',
                        '@type': 'ItemList',
                        name: 'Centurion Matting Products Catalog',
                        url: ABS_URL('/catalog'),
                        itemListElement: CATALOG.map((c, i) => ({
                            '@type': 'ListItem',
                            position: i + 1,
                            item: {
                                '@type': 'Product',
                                name: c.head,
                                brand: { '@type': 'Brand', name: SITE.name },
                                url: c.to.startsWith('/quote') || c.to.startsWith('/contact')
                                    ? ABS_URL('/catalog')
                                    : ABS_URL(c.to),
                                description: c.body,
                            },
                        })),
                    },
                ]}
            />

            <HeroPlaceholder
                mediaKey="millYard"
                imageUrl={hero.image_url}
                eyebrow={hero.eyebrow}
                title={hero.headline}
                subtitle={hero.body}
            />

            <section className="mx-auto max-w-4xl px-6 py-20 md:py-28">
                <span className="eyebrow">Overview</span>
                <p className="mt-8 font-sans text-lg leading-relaxed text-brand-text">
                    Centurion Matting Products manufactures and wholesales the full spectrum of timber ground-protection matting from our Chemainus, BC mill, coordinated through our Calgary, AB sales office. Whether you’re an owner, a Tier 1 contractor, a matting service company, or a First Nations partner organization - every Centurion mat is built from coastal Hem-Fir Hi-Line #2, manufactured direct, and engineered for Western Canada’s most difficult terrain.
                </p>
                <p className="mt-6 font-sans text-base leading-relaxed text-brand-muted">
                    Browse the catalog below or use the field calculator to spec your job. Custom sizes and engineered specifications are always available on request.
                </p>
            </section>

            {/* Catalog grid */}
            <section className="border-y border-brand-hairline bg-brand-surface" data-eyebrow-tone="blue">
                <div className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-24">
                    <span className="eyebrow">Catalog</span>
                    <h2 className="h-display mt-6 max-w-3xl text-3xl sm:text-4xl">
                        Six product families. One mill standard.
                    </h2>
                    <div className="mt-12 grid gap-px bg-brand-hairline md:grid-cols-2 lg:grid-cols-3" data-testid="catalog">
                        {CATALOG.map((c, i) => (
                            <Link
                                key={c.head}
                                to={c.to}
                                data-testid={`catalog-${i}`}
                                className="group flex flex-col gap-3 bg-brand-surface p-6 transition-colors duration-150 hover:bg-brand-elevated md:p-8"
                            >
                                <h3 className="font-display text-lg font-bold uppercase tracking-tight text-brand-text">
                                    {c.head}
                                </h3>
                                <p className="font-sans text-sm leading-relaxed text-brand-muted">
                                    {c.body}
                                </p>
                                <span className="mt-3 inline-flex items-center gap-2 font-display text-[11px] font-bold uppercase tracking-[0.18em] text-brand-blue group-hover:text-brand-gold">
                                    {c.cta}
                                    <ArrowRight
                                        size={12}
                                        className="transition-transform duration-150 group-hover:translate-x-1"
                                    />
                                </span>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Spec comparison table */}
            <section className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-24">
                <span className="eyebrow">Spec Comparison</span>
                <h2 className="h-display mt-6 max-w-3xl text-3xl sm:text-4xl">
                    Pick the right product for the job.
                </h2>
                <div className="mt-10 overflow-x-auto" data-testid="spec-table">
                    <table className="w-full min-w-[760px] border-collapse text-left">
                        <thead className="bg-brand-elevated">
                            <tr>
                                <th className="border-b border-brand-hairline p-4 font-display text-[10px] font-bold uppercase tracking-[0.2em] text-brand-gold">Product</th>
                                <th className="border-b border-brand-hairline p-4 font-display text-[10px] font-bold uppercase tracking-[0.2em] text-brand-gold">Dimensions</th>
                                <th className="border-b border-brand-hairline p-4 font-display text-[10px] font-bold uppercase tracking-[0.2em] text-brand-gold">Material</th>
                                <th className="border-b border-brand-hairline p-4 font-display text-[10px] font-bold uppercase tracking-[0.2em] text-brand-gold">Common applications</th>
                                <th className="border-b border-brand-hairline p-4 font-display text-[10px] font-bold uppercase tracking-[0.2em] text-brand-gold">Lead time</th>
                            </tr>
                        </thead>
                        <tbody>
                            {SPEC_TABLE.map((r, i) => (
                                <tr key={r.product} className={i % 2 ? 'bg-brand-base' : 'bg-brand-surface'}>
                                    <td className="border-b border-brand-hairline p-4 font-display text-sm font-bold text-brand-text">{r.product}</td>
                                    <td className="border-b border-brand-hairline p-4 font-sans text-sm tabular-nums text-brand-text">{r.dim}</td>
                                    <td className="border-b border-brand-hairline p-4 font-sans text-sm text-brand-muted">{r.material}</td>
                                    <td className="border-b border-brand-hairline p-4 font-sans text-sm text-brand-muted">{r.apps}</td>
                                    <td className="border-b border-brand-hairline p-4 font-sans text-sm text-brand-muted">{r.lead}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Wholesale / Reseller program */}
            <section className="border-y border-brand-hairline bg-brand-surface">
                <div className="mx-auto max-w-7xl grid gap-12 px-6 py-20 md:grid-cols-[1fr_1.4fr] md:px-10 md:py-24 md:gap-16">
                    <div>
                        <span className="eyebrow">Wholesale & Resellers</span>
                        <h2 className="h-display mt-6 text-2xl sm:text-3xl">
                            Source matting Manufacturer Direct.
                        </h2>
                    </div>
                    <div>
                        <p className="font-sans text-base leading-relaxed text-brand-muted">
                            Centurion supplies matting service companies and resellers across Western Canada with manufacturer-direct pricing, custom-spec capability, and predictable mill-schedule lead times. If your company resells timber matting and you’d like to source direct, contact our procurement team.
                        </p>
                        <Link
                            to="/partnerships/procurement"
                            data-testid="suppliers-procurement-link"
                            className="mt-6 inline-flex items-center gap-2 bg-brand-gold px-5 py-3 font-display text-xs font-bold uppercase tracking-[0.16em] text-brand-base transition-colors duration-150 hover:bg-brand-gold-hover"
                        >
                            Procurement Partnerships
                            <ArrowRight size={12} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Calculator */}
            <section className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-24" id="calculator">
                <span className="eyebrow">Field Calculator</span>
                <h2 className="h-display mt-6 max-w-3xl text-3xl sm:text-4xl">
                    Spec your job in 30 seconds.
                </h2>
                <div className="mt-10">
                    <MattingCalculator variant="compact" />
                </div>
            </section>

            {/* FAQ */}
            <section className="border-y border-brand-hairline bg-brand-surface">
                <div className="mx-auto max-w-4xl px-6 py-20 md:py-28">
                    <span className="eyebrow">FAQ</span>
                    <h2 className="h-display mt-6 text-3xl sm:text-4xl">
                        Common questions about the Centurion catalog.
                    </h2>
                    <div className="mt-10">
                        <FaqAccordion faqs={FAQS} testId="faq-suppliers" />
                    </div>
                </div>
            </section>

            <RelatedBand
                heading="Adjacent product hubs."
                items={[
                    { to: '/access-mats', eyebrow: 'Access', title: 'Access Mats', body: 'Traditional three-ply interlocking access mat.' },
                    { to: '/rig-mats', eyebrow: 'Rig', title: 'Rig Mats', body: 'Heavy-duty timber decks for drilling, workover, frac, and pipeline skids.' },
                    { to: '/crane-mats', eyebrow: 'Crane', title: 'Crane Mats', body: 'Laminated coastal Fir for crawler crane outriggers and heavy-lift picks.' },
                ]}
            />

            <CtaBand
                eyebrow="Manufacturer Direct"
                title="Spec your job. We'll quote within a business day."
                body="Standard sizes or built-to-spec - Manufacturer Direct from our Chemainus, BC mill."
                cta={{ to: '/quote', label: 'Get a Manufacturer Direct Quote' }}
                secondary={{ to: '/contact?topic=partnership', label: 'Contact Procurement' }}
            />
        </>
    );
}
